import { fetchApi, ApiError } from "./api";
import { API_ENDPOINTS } from "../config/api";

/** Job status from GET /api/jobs/{job_id} */
export type JobStatus =
  | "queued"
  | "in_progress"
  | "complete"
  | "failed"
  | "not_found";

export interface JobResponse<T = unknown> {
  job_id: string;
  status: JobStatus;
  result?: T;
  error?: string;
  has_binary_result?: boolean;
}

/** Callback for progress during polling (queued | in_progress). */
export type JobProgressStatus = "queued" | "in_progress";

export interface PollJobOptions {
  maxWaitMs?: number;
  pollIntervalMs?: number;
  /** Called when job status is queued or in_progress so UI can show "Scrape in progress…" */
  onStatus?: (status: JobProgressStatus) => void;
}

const DEFAULT_MAX_WAIT_MS = 10 * 60 * 1000; // 10 minutes
const DEFAULT_POLL_INTERVAL_MS = 2000;

/**
 * Poll GET /api/jobs/{job_id} until status is complete or failed (or timeout).
 * Returns the result on success or throws with the error message on failure.
 */
export async function pollJobUntilComplete<T = unknown>(
  jobId: string,
  options: PollJobOptions = {},
): Promise<T> {
  const maxWaitMs = options.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const start = Date.now();

  while (Date.now() - start < maxWaitMs) {
    let data: JobResponse<T>;
    try {
      data = await fetchApi<JobResponse<T>>(API_ENDPOINTS.job(jobId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        throw new Error("Job not found or expired");
      }
      throw err;
    }

    if (data.status === "complete") {
      if (data.result === undefined) {
        throw new Error("Job completed but no result returned");
      }
      return data.result;
    }

    if (data.status === "failed") {
      throw new Error(data.error ?? "Job failed");
    }

    if (data.status === "not_found") {
      throw new Error("Job not found or expired");
    }

    // queued | in_progress: report progress and keep polling
    if (data.status === "queued" || data.status === "in_progress") {
      options.onStatus?.(data.status);
    }

    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }

  throw new Error("Job timed out");
}

/** Job list item from GET /api/jobs/. */
export interface JobListItem {
  job_id: string;
  function: string;
  status: JobStatus;
  enqueue_time?: string | null;
  message?: string;
  created_at?: string;
  [key: string]: unknown;
}

/** Response from GET /api/jobs/. */
export interface JobListResponse {
  jobs: JobListItem[];
  total: number;
}

/**
 * Get job status and result (GET /api/jobs/{job_id}).
 */
export async function getJobStatus(jobId: string): Promise<JobResponse> {
  return fetchApi<JobResponse>(API_ENDPOINTS.job(jobId));
}

/**
 * List all jobs (GET /api/jobs/).
 */
export async function listJobs(): Promise<JobListResponse> {
  const data = await fetchApi<JobListResponse>(API_ENDPOINTS.jobsList);
  return {
    jobs: Array.isArray(data?.jobs) ? data.jobs : [],
    total: typeof data?.total === "number" ? data.total : 0,
  };
}

/** Allowed scope for DELETE /api/jobs/?scope=... */
export type JobDeleteScope = "queued" | "in_progress" | "all";

/**
 * Delete jobs by scope (DELETE /api/jobs/?scope=...).
 * - queued: only queued jobs (default)
 * - in_progress: only in-progress jobs
 * - all: queued and in-progress jobs
 */
export async function deleteJobs(
  scope: JobDeleteScope = "queued",
): Promise<void> {
  const url = `${API_ENDPOINTS.jobsList}?scope=${encodeURIComponent(scope)}`;
  await fetchApi<void>(url, { method: "DELETE" });
}
